import { observable, action, makeObservable, flow } from 'mobx';
import {
  fetchSchedulerAuth, fetchSchedulerActions, fetchAllUserActions, createSchedulerAction, updateSchedulerActions,
  updateSchedulerAction, updateSchedulerFlowsteps, deleteSchedulerActions, logoutScheduler, updateSchedulerFlows,
  getManpackInstallation, getManpackFlow,
  getRedirectionUrl
} from 'src/api';
import office from 'src/services/office';
import { ws } from 'src/api/ws';
import * as Flow from 'shared/utils/flow';
import { FlowstepType } from 'shared/utils/flowstep';
import { mergeLists, MergeListsAction, asArray, pick, reflect } from 'shared/utils/utils';
import { openPopupWindow } from 'src/services/tools';
import { migrateToTheLatest as migrateFlowsToTheLastest } from 'shared/migration/flow';
import { migrateToTheLatest as migrateFlowstepsToTheLatest } from 'shared/migration/flowstep';
// @rollback
// import { rollbackToLegacy as migrateFlowstepsToTheLatest } from 'shared/migration/flowstep';
import { migrateToTheLatest as migrateActionsToTheLatest } from 'shared/migration/action';

export default class DataStore {
  @observable flowSteps = [];
  @observable flows = [];

  @observable scheduler = {};
  @observable actions = [];

  @observable areAllUserActionsActual = false;
  @observable onlyOnActiveWorksheet = true;
  @observable allUserActions = [];

  @observable selectedFlowId = '';

  userStore = null;

  constructor() {
    makeObservable(this);
  }

  linkStores(stores) {
    Object.assign(this, stores);
  }

  @action async clean() {
    this.flowSteps = [];
    this.flows = [];

    this.scheduler = {};
    this.actions = [];
    this.allUserActions = [];
  }

  @action async startup() {
    await this.fetchFlowsteps();
    await this.fetchFlows();
    await this.fetchSchedulerAuth();
    await this.getOnlyOnActiveWorksheet();
  }

  @action async getOnlyOnActiveWorksheet() {
    this.onlyOnActiveWorksheet = await office.getOnlyOnActiveWorksheet();
  }

  @action async setOnlyOnActiveWorksheet(value) {
    this.onlyOnActiveWorksheet = value;
    await office.setOnlyOnActiveWorksheet(value);
  }

  @action async fetchFlowsteps() {
    const original = await office.getFlowstep();
    const { objects, isAnyMigrated } = migrateFlowstepsToTheLatest(original);
    if (isAnyMigrated) {
      await office.replaceFlowsteps(objects);
    }

    this.flowSteps = objects;
  }

  getFlowstep(id) {
    return this.flowSteps.find(item => id === item.id);
  }

  @action async createOrUpdateFlowstep(flowstep, flowId) {
    this.requestStore.setText('Saving flow step ...');

    const id = this.createOrUpdateFlowstepNoSync(flowstep, flowId);
    await this.syncOfficeAndSchedulerWithStore();
    await this.contextStore.reloadWorksheetContext(flowstep.worksheetId); // Force fetch
    return id;
  }

  @action async moveFlowstepToFlow(flowstep, fromFlowId, toFlowId) {
    this.requestStore.setText('Moving flow step ...');

    const fromFlow = this.getFlow(fromFlowId);
    const toFlow = this.getFlow(toFlowId);

    this.deleteFlowstepFromFlow(flowstep, fromFlow);
    this.addFlowstepToFlow(flowstep, toFlow);

    await this.syncOfficeAndSchedulerWithStore();
    return toFlowId;
  }

  @action async deleteFlowstep(flowstepOrFlowsteps, flowId) {
    this.requestStore.setText('Deleting flow step ...');

    this.deleteFlowstepFromFlow(flowstepOrFlowsteps, this.getFlow(flowId));
    this.updateFlowstepList(flowstepOrFlowsteps, MergeListsAction.Remove);

    await this.syncOfficeAndSchedulerWithStore();

    const ids = asArray(flowstepOrFlowsteps).map(x => x.worksheetId);
    await this.contextStore.reloadWorksheetContext(ids); // Force fetch
  }

  createOrUpdateFlowstepNoSync(flowstep, flowId) {
    const action = this.getFlowstep(flowstep.id) ? MergeListsAction.Replace : MergeListsAction.Add;
    this.updateFlowstepList(flowstep, action);
    if (action === MergeListsAction.Replace) {
      return flowId;
    }

    const flow = flowId
      ? this.getFlow(flowId)
      : this.getFlowByFlowstepCategory(flowstep.category);
    this.addFlowstepToFlow(flowstep, flow);
    return flow.id;
  }

  addFlowstepToFlow(flowstep, flow) {
    const flowsteps = [...flow.options.flowsteps, { id: flowstep.id }];
    const newFlow = { ...flow, options: { ...flow.options, flowsteps } };
    this.updateFlowList(newFlow, MergeListsAction.Replace);
    return newFlow;
  }

  deleteFlowstepFromFlow(flowstepOrFlowsteps, flowOrFlowId) {
    const flow = typeof flowOrFlowId === 'object'
      ? flowOrFlowId
      : this.getFlow(flowOrFlowId);
    const flowsteps = mergeLists(flow.options.flowsteps, asArray(flowstepOrFlowsteps), MergeListsAction.Remove, idEqComparator);
    const newFlow = { ...flow, options: { ...flow.options, flowsteps } };
    this.updateFlowList(newFlow, MergeListsAction.Replace);
    return newFlow;
  }

  updateFlowstepList(flowstepOrFlowsteps, action) {
    this.flowSteps = mergeLists(this.flowSteps, asArray(flowstepOrFlowsteps), action, idEqComparator);
  }


  // Flows

  @action async fetchFlows() {
    let areMigrated = false;
    let flows = await office.getFlows();
    // console.log('DataStore::fetchFlows: ', flows);

    if (!flows.find(x => x.type === Flow.Type.Pull || x.type === Flow.Type.Push)) {
      flows = await this.migrateFlowstepsToFlows();
      areMigrated = true;
    }

    const { objects, isAnyMigrated } = migrateFlowsToTheLastest(flows);
    if (isAnyMigrated || areMigrated) {
      await office.replaceFlows(objects);
    }

    this.flows = objects;
  }

  getFlow(id) {
    return this.flows.find(x => id === x.id);
  }

  getFlowByFlowstepCategory(category) {
    return this.flows.find(x => x.type === category);
  }

  getFlowByFlowstep(flowstepId) {
    return this.flows.find(x => x.options.flowsteps.some(y => y.id === flowstepId));
  }

  @action async createOrUpdateFlow(flow) {
    this.requestStore.setText('Saving flow ...');

    this.updateFlowList(flow, MergeListsAction.ReplaceAndMerge);
    await this.syncOfficeAndSchedulerWithStore();
  }

  @action async deleteFlow(flowOrFlows) {
    this.requestStore.setText('Deleting flow ...');

    const flows = asArray(flowOrFlows);
    const flowsteps = flows.flatMap(x => x.options.flowsteps);

    this.updateFlowList(flows, MergeListsAction.Remove);
    this.updateFlowstepList(flowsteps, MergeListsAction.Remove);

    await this.syncOfficeAndSchedulerWithStore();
  }

  @action async replaceFlows(flows) {
    this.requestStore.setText('Updating flows ...');

    this.flows = flows;
    await this.syncOfficeAndSchedulerWithStore();
  }

  async migrateFlowstepsToFlows() {
    const flowstepsToFlow = (flowType, name) => {
      return {
        id: Flow.generateUuid(),
        type: flowType,
        name,
        options: {
          flowsteps: this.flowSteps.reduce((out, { id, category }) => {
            category === flowType && out.push({ id });
            return out;
          }, []),
        },
      }
    }

    return [
      flowstepsToFlow(Flow.Type.Pull, 'Pull Data'),
      flowstepsToFlow(Flow.Type.Push, 'Push Data')
    ];
  }

  @action async syncOfficeAndSchedulerWithStore() {
    await office.replaceFlows(this.flows);
    await office.replaceFlowsteps(this.flowSteps);
    await this.updateSchedulerData();
  }

  updateFlowList(flowOrFlows, action) {
    this.flows = mergeLists(this.flows, asArray(flowOrFlows), action, idEqComparator);
  }


  // Scheduler actions

  @action async fetchSchedulerAuth() {
    if (this.scheduler.document_id) { return; }
    if (!this.userStore.license.scheduler.use.can) { return; }

    const { status, data } = await reflect(office.getDocumentOnlinePath());
    if (status === 'rejected') { return; }

    this.scheduler.document_path = data;
    const result = await fetchSchedulerAuth({ document: data }, this.userStore.token);

    if (!result.document_id) { return result; }

    this.scheduler = result;
    await this.fetchActions();
    await this.updateSchedulerData();
  }

  @action async loginSchedulerDocument(cancelToken) {
    const { key, url } = await ws.action('ms-authorise', { path: this.scheduler.document_path }, { cancelToken });
    await openPopupWindow(getRedirectionUrl({ url }));
    this.scheduler = await ws.waitFor(key, { cancelToken });

    await this.fetchActions();
    await this.updateSchedulerData();
  }

  @action async logoutSchedulerDocument() {
    await logoutScheduler({ document_id: this.scheduler.document_id }, this.userStore.token);
    this.scheduler = { document_path: this.scheduler.document_path };
  }

  @action async checkDocumentChanged(notify = false) {
    const oldPath = this.scheduler.document_path;
    if (!oldPath) { return; }

    const { status, data: newPath } = await reflect(office.getDocumentOnlinePath());
    if (status === 'rejected') { return; }
    if (newPath === oldPath) { return; }

    this.scheduler = { document_path: newPath };
    if (notify) {
      this.requestStore.warn('You need to re-login because document has been changed');
    }
  }

  @action async fetchActions() {
    const actions = await fetchSchedulerActions({ document_id: this.scheduler.document_id }, this.userStore.token);
    this.actions = migrateActionsToTheLatest(actions).objects;
    this.clearAllUserActions();
  }

  async syncActionsWithFlows() {
    const result = this.actions.reduce((out, x) => {
      const hadFlow = !!x.flow_id; // migration from actions to flows
      const flow_id = x.flow_id || this.flows.find(flow => flow.type === x.action)?.id;
      const flow = flow_id ? this.getFlow(flow_id) : null;
      const isOwned = this.userStore.user.username === x.owner_name && x.owner_name;

      if (flow) {
        const newAction = { ...x, flow_id };
        out.filtered.push(newAction);
        !hadFlow && isOwned && out.migrated.push(newAction);
      }
      else if (isOwned) {
        out.removed.push(x);
      }

      return out;
    }, { removed: [], migrated: [], filtered: [] });

    // console.log('DataStore::syncActionsWithFlows: ', result);

    const { filtered, removed, migrated } = result;
    this.actions = filtered;
    const { document_id } = this.scheduler;
    if (!document_id) { return; }

    if (removed.length) {
      await deleteSchedulerActions({ actions: removed.map(x => ({ id: x.id, document_id })) }, this.userStore.token);
      this.clearAllUserActions();
    }

    if (migrated.length) {
      await updateSchedulerActions({ actions: migrated.map(action => ({ action, document_id })) }, this.userStore.token);
      this.clearAllUserActions();
    }
  }

  @action async fetchAllUserActions() {
    if (!this.areAllUserActionsActual) {
      const actions = await fetchAllUserActions(this.userStore.token);
      this.allUserActions = migrateActionsToTheLatest(actions).objects;
      this.areAllUserActionsActual = true;
    }
    return this.allUserActions;
  }

  @action async createAction(data) {
    await this.checkDocumentChanged(true);
    if (!this.scheduler.document_id) { return; }

    const action = {
      ...data,
      ...await createSchedulerAction({ document_id: this.scheduler.document_id, ...data }, this.userStore.token)
    };
    this.actions = [...this.actions, action];
    this.clearAllUserActions();
  }

  @action async updateAction(action) {
    await this.checkDocumentChanged(true);
    if (!this.scheduler.document_id) { return; }

    const updatedAction = await updateSchedulerAction({ document_id: this.scheduler.document_id, action }, this.userStore.token);
    this.actions = this.actions.map(item => item.id === updatedAction.id ? updatedAction : item);
  }

  @action async updateActions(actions) {
    const updatedActions = (await updateSchedulerActions({ actions }, this.userStore.token))
      .map((updated, index) => ({ ...actions[index], ...updated }));
    this.actions = this.actions.map(item => updatedActions.find(x => x.id === item.id) ?? item);
    // this.clearAllUserActions();
  }

  @action async deleteAction(action) {
    await this.checkDocumentChanged(true);
    if (!this.scheduler.document_id) { return; }

    await this.deleteActions([{ document_id: this.scheduler.document_id, id: action.id }]);
  }

  @action async deleteActions(actions) {
    await deleteSchedulerActions({ actions }, this.userStore.token);
    this.actions = this.actions.filter(item => !actions.find(x => x.id === item.id));
    this.clearAllUserActions();
  }

  @action clearAllUserActions() {
    this.allUserActions = [];
    this.areAllUserActionsActual = false;
  }

  @action async updateSchedulerData() {
    await this.checkDocumentChanged();
    await this.updateSchedulerFlowsteps();
    await this.updateSchedulerFlows();
  }

  @action async updateSchedulerFlowsteps() {
    const { document_id } = this.scheduler;
    if (!document_id) { return }

    const common = ['lastUpdateTimestampCell', 'errorReaction', 'createDataTable'];
    const flowsteps = this.flowSteps.map(item => {
      const getOptions = () => {
        switch (item.type) {
          case FlowstepType.Soql: return pick(item.options, [...common, 'query', 'queryAll', 'showLabels']);
          case FlowstepType.Report: return pick(item.options, [...common, 'report_id', 'report_name', 'excludedFields']);
          case FlowstepType.Csv: return pick(item.options, [...common, 'url']);
          default: return item.options;
        }
      }

      return { ...item, options: getOptions() };
    });

    // TODO: do we need to somehow map flowsteps at all? What the point?
    await updateSchedulerFlowsteps({ document_id, flowsteps }, this.userStore.token);
  }

  @action async updateSchedulerFlows() {
    const { document_id } = this.scheduler;
    if (document_id) {
      const flows = Flow.joinFlowsteps(this.flows, this.flowSteps);
      await updateSchedulerFlows({ document_id, flows }, this.userStore.token);
      await this.syncActionsWithFlows();
    }
  }

  async isManpackInstalled() {
    const manpack = await getManpackInstallation(this.userStore.token);
    return manpack.installed;
  }

  async getManpackRegisteredFlow(objectName) {
    return await getManpackFlow({ object: objectName, document_id: this.scheduler.document_id }, this.userStore.token);
  }
}


function idEqComparator(a, b) {
  return a.id === b.id;
}
